/** ManagerChat (P5.5) — the manager's chat panel with image/file attachments.
 *
 * Adapted from the prebuilt `<Chat>` (which has no file picker at all) and its `<ChatEntry>`, because
 * both fall short for attachments:
 *   1. `<Chat>`'s form only sends text — `useChat().send` accepts `{ attachments }` (livekit-client
 *      `sendText` writes one byte stream per file on the same `lk.chat` topic) but nothing in the prefab
 *      lets a manager pick one;
 *   2. `<ChatEntry>` renders attachments behind `file.type.startsWith('image/')`, yet components-react
 *      rebuilds every RECEIVED file as `new File(chunks, name)` with no type — so `type` is `''` and a
 *      received image renders as nothing. `attachments.ts` resolves the type from the file name instead,
 *      and non-images get a download link (the prefab drops them entirely).
 * Everything else (message list, unread badge, the `useChat` plumbing, the layout context) is reused. */
import {
  CHAT_ATTACHMENT_ACCEPT,
  CHAT_ATTACHMENT_MAX_BYTES,
  CHAT_ATTACHMENT_MAX_COUNT,
  CHAT_ATTACHMENT_ORDER_ATTR,
  encodeAttachmentOrder,
  formatFileSize,
  isChatImageMimeType,
  orderAttachments,
} from "@evas/shared";
import {
  ChatCloseIcon,
  type MessageFormatter,
  type ReceivedChatMessage,
  useChat,
  useMaybeLayoutContext,
} from "@livekit/components-react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { pickAttachments, rejectionMessage, resolveAttachmentType } from "./attachments.js";

/** One received/sent attachment. Images render inline (click for full size); everything else becomes a
 * download link. Object URLs are created per render pass and revoked on unmount. */
function Attachment({ file }: { file: File }) {
  const mimeType = resolveAttachmentType(file);
  const [url, setUrl] = useState<string | null>(null);

  useEffect(() => {
    const next = URL.createObjectURL(file);
    setUrl(next);
    return () => URL.revokeObjectURL(next);
  }, [file]);

  if (!url) return null;
  if (isChatImageMimeType(mimeType)) {
    return (
      <a href={url} target="_blank" rel="noreferrer" className="manager-chat-image-link">
        <img className="manager-chat-image" src={url} alt={file.name} />
      </a>
    );
  }
  return (
    <a
      className="manager-chat-file"
      href={url}
      download={file.name}
      target="_blank"
      rel="noreferrer"
      title={file.name}
    >
      <span className="manager-chat-file-name">{file.name}</span>
      {file.size > 0 && <span className="manager-chat-file-size">{formatFileSize(file.size)}</span>}
    </a>
  );
}

/** A single chat line — the prebuilt entry's layout plus attachment rendering. */
function Entry({
  entry,
  hideName,
  messageFormatter,
}: {
  entry: ReceivedChatMessage;
  hideName: boolean;
  messageFormatter?: MessageFormatter;
}) {
  const who = entry.from?.name ?? entry.from?.identity;
  const time = new Date(entry.timestamp);
  // Restore the sender's order: components-core pairs byte streams to their message with
  // `mergeMap(attachments.values())`, which emits in COMPLETION order — so `attachedFiles` is sorted by
  // whichever file downloaded fastest. Observed on dev: images sent large→small rendered "second, third,
  // first". The sender states the true order in an attribute; a peer that doesn't (older bundle) simply
  // keeps arrival order.
  const files = useMemo(
    () =>
      orderAttachments(entry.attachedFiles ?? [], entry.attributes?.[CHAT_ATTACHMENT_ORDER_ATTR]),
    [entry.attachedFiles, entry.attributes],
  );
  // An attachment-only message carries a single-space body (see `attachmentSafeBody` in widget-core:
  // an empty text stream writes no chunks, and this library's rxjs `reduce` over the chunks would then
  // never emit, dropping the whole message). Treat a whitespace-only body as no text.
  const body = entry.message.trim();
  const formatted = useMemo(
    () => (messageFormatter ? messageFormatter(body) : body),
    [body, messageFormatter],
  );

  return (
    <li
      className="lk-chat-entry"
      title={time.toLocaleTimeString(undefined, { timeStyle: "full" })}
      data-lk-message-origin={entry.from?.isLocal ? "local" : "remote"}
    >
      {!hideName && (
        <span className="lk-meta-data">
          <strong className="lk-participant-name">{who}</strong>
          <span className="lk-timestamp">
            {time.toLocaleTimeString(undefined, { timeStyle: "short" })}
          </span>
        </span>
      )}
      {body && <span className="lk-message-body">{formatted}</span>}
      {files.length > 0 && (
        <span className="manager-chat-attachments">
          {files.map((f, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: a delivered message's file list never changes, and two files in one message may share a name — the index is what disambiguates them.
            <Attachment key={`${entry.id}-${f.name}-${i}`} file={f} />
          ))}
        </span>
      )}
    </li>
  );
}

/** How close to the bottom still counts as "following the conversation" (px). Absorbs sub-pixel and
 * browser-zoom rounding in `scrollTop`, which is fractional on high-DPI displays. */
const SCROLL_STICK_SLACK_PX = 24;

export interface ManagerChatProps {
  messageFormatter?: MessageFormatter;
  style?: React.CSSProperties;
}

export function ManagerChat({ messageFormatter, style }: ManagerChatProps) {
  const { chatMessages, send, isSending } = useChat();
  const layoutContext = useMaybeLayoutContext();
  const listRef = useRef<HTMLUListElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const lastReadAt = useRef(0);
  // Queued attachments carry a stable key: the chip row is spliced on removal, so a list index would
  // re-key the surviving chips and React would reuse the wrong DOM node.
  const [pending, setPending] = useState<Array<{ key: string; file: File }>>([]);
  const [error, setError] = useState<string | null>(null);
  const nextKey = useRef(0);

  // Keep the newest message in view. A ResizeObserver rather than a message-count effect, because an
  // attachment message grows AFTER it mounts: the <img> has no height until the blob decodes, so
  // scrolling on the message count alone left an image below the fold until the manager scrolled by hand
  // (the prebuilt <Chat> has the same flaw — it has no attachments to grow). Observing the list's own
  // scrollHeight covers both cases: a new text message and an image that lays out late.
  useEffect(() => {
    const el = listRef.current;
    if (!el) return;
    // Only auto-scroll when the manager is already AT the bottom. Otherwise a late-decoding image (or any
    // resize) would yank someone who has deliberately scrolled up to read history back down. The
    // threshold absorbs sub-pixel/zoom rounding in scrollTop.
    const atBottom = () =>
      el.scrollHeight - el.scrollTop - el.clientHeight <= SCROLL_STICK_SLACK_PX;
    let stuck = true; // a freshly opened panel starts pinned to the newest message
    const stick = () => {
      if (stuck) el.scrollTo({ top: el.scrollHeight });
    };
    const onScroll = () => {
      stuck = atBottom();
    };
    stick();
    el.addEventListener("scroll", onScroll, { passive: true });
    // Two observers, because neither alone is sufficient:
    //   - MutationObserver(childList) fires when an entry is appended. Observing the LIST's own box would
    //     NOT work: `.lk-chat-messages` is `max-height:100%; overflow:auto`, so once the chat fills, its
    //     box stops changing and only `scrollHeight` grows — precisely when scrolling matters most.
    //   - ResizeObserver on each ENTRY fires when that entry grows, which is how a late-decoding image
    //     (no height until the blob decodes) gets scrolled into view. Entries are height:auto, so they do
    //     change box size. Re-observing an already-observed element is a no-op.
    const sizes = new ResizeObserver(stick);
    const observeEntries = () => {
      for (const entry of el.children) sizes.observe(entry);
    };
    observeEntries();
    const added = new MutationObserver(() => {
      observeEntries();
      stick();
    });
    added.observe(el, { childList: true });
    return () => {
      el.removeEventListener("scroll", onScroll);
      sizes.disconnect();
      added.disconnect();
    };
  }, []);

  // Unread badge (same bookkeeping as the prebuilt Chat).
  useEffect(() => {
    if (!layoutContext || chatMessages.length === 0) return;
    const newest = chatMessages[chatMessages.length - 1]?.timestamp ?? 0;
    if (layoutContext.widget.state?.showChat && newest !== lastReadAt.current) {
      lastReadAt.current = newest;
      return;
    }
    const unread = chatMessages.filter((m) => m.timestamp > lastReadAt.current).length;
    if (unread > 0 && layoutContext.widget.state?.unreadMessages !== unread) {
      layoutContext.widget.dispatch?.({ msg: "unread_msg", count: unread });
    }
  }, [chatMessages, layoutContext]);

  const onPick = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(e.target.files ?? []);
      e.target.value = ""; // allow re-picking the same file after a removal
      if (picked.length === 0) return;
      const { accepted, rejection } = pickAttachments(picked, pending.length);
      if (accepted.length > 0) {
        setPending((cur) => [
          ...cur,
          ...accepted.map((file) => ({ key: `f${nextKey.current++}`, file })),
        ]);
      }
      setError(
        rejection
          ? rejectionMessage(rejection, {
              maxBytes: CHAT_ATTACHMENT_MAX_BYTES,
              maxCount: CHAT_ATTACHMENT_MAX_COUNT,
            })
          : null,
      );
    },
    [pending.length],
  );

  const submit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      const body = inputRef.current?.value.trim() ?? "";
      // Attachment-only messages are valid; fully empty ones are not.
      if (!body && pending.length === 0) return;
      try {
        const files = pending.map((p) => p.file);
        await send(
          body,
          files.length > 0
            ? {
                attachments: files,
                // State the intended order: components-core pairs byte streams with mergeMap, so
                // `attachedFiles` arrives in DOWNLOAD-completion order, not send order.
                attributes: { [CHAT_ATTACHMENT_ORDER_ATTR]: encodeAttachmentOrder(files) },
              }
            : undefined,
        );
        if (inputRef.current) inputRef.current.value = "";
        setPending([]);
        setError(null);
        inputRef.current?.focus();
      } catch (err) {
        console.error("chat send failed", err);
        setError("Couldn't send that message. Please try again.");
      }
    },
    [send, pending],
  );

  return (
    <div className="lk-chat" style={style}>
      <div className="lk-chat-header">
        Messages
        {layoutContext && (
          <button
            type="button"
            className="lk-close-button"
            onClick={() => layoutContext.widget.dispatch?.({ msg: "toggle_chat" })}
            aria-label="Close chat"
          >
            <ChatCloseIcon />
          </button>
        )}
      </div>
      <ul className="lk-list lk-chat-messages" ref={listRef}>
        {chatMessages.map((m, i, all) => (
          <Entry
            key={m.id ?? i}
            entry={m}
            // Collapse the name/timestamp header on consecutive messages from the same sender.
            hideName={i >= 1 && all[i - 1]?.from === m.from}
            messageFormatter={messageFormatter}
          />
        ))}
      </ul>
      {pending.length > 0 && (
        <div className="manager-chat-pending">
          {pending.map(({ key, file }) => (
            <span key={key} className="manager-chat-chip">
              <span className="manager-chat-chip-name">{file.name}</span>
              <button
                type="button"
                className="manager-chat-chip-x"
                onClick={() => {
                  setPending((cur) => cur.filter((p) => p.key !== key));
                  setError(null);
                }}
                title="Remove"
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>
      )}
      {error && <div className="manager-chat-error">{error}</div>}
      <form className="lk-chat-form" onSubmit={submit}>
        <input
          ref={fileRef}
          type="file"
          multiple
          accept={CHAT_ATTACHMENT_ACCEPT}
          className="manager-chat-file-input"
          onChange={onPick}
        />
        <button
          type="button"
          className="lk-button manager-chat-attach"
          onClick={() => fileRef.current?.click()}
          disabled={isSending || pending.length >= CHAT_ATTACHMENT_MAX_COUNT}
          title="Attach an image or file"
          aria-label="Attach an image or file"
        >
          <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor" aria-hidden="true">
            <path d="M4.5 3a2.5 2.5 0 0 1 5 0v9a1.5 1.5 0 0 1-3 0V5a.5.5 0 0 1 1 0v7a.5.5 0 0 0 1 0V3a1.5 1.5 0 1 0-3 0v9a2.5 2.5 0 0 0 5 0V5a.5.5 0 0 1 1 0v7a3.5 3.5 0 1 1-7 0z" />
          </svg>
        </button>
        <input
          className="lk-form-control lk-chat-form-input"
          disabled={isSending}
          ref={inputRef}
          type="text"
          placeholder="Enter a message..."
          onInput={(e) => e.stopPropagation()}
          onKeyDown={(e) => e.stopPropagation()}
          onKeyUp={(e) => e.stopPropagation()}
        />
        <button type="submit" className="lk-button lk-chat-form-button" disabled={isSending}>
          Send
        </button>
      </form>
    </div>
  );
}
